/**
 * Configuração de runtime do SPA.
 *
 * 🔴 As variáveis abaixo são INJETADAS pela plataforma no build; o projeto apenas
 * LÊ. A ausência é ERRO, não um default — um default apontaria para uma API ou um
 * User Pool que não existem e o app subiria quebrado (mesma regra do
 * ECR_IMAGE_URI da IaC).
 *
 * Nomes lógicos do contrato de infra × nome Vite (exige prefixo `VITE_` para
 * expor ao bundle do browser):
 *   - VITE_API_URL            → `VITE_API_URL`            (URL do API Gateway)
 *   - USER_POOL_CLIENT_ID     → `VITE_USER_POOL_CLIENT_ID` (app client público do Cognito)
 *   - USER_POOL_ID            → `VITE_USER_POOL_ID`        (User Pool do Cognito)
 *   - AWS_REGION              → `VITE_AWS_REGION`          (região sa-east-1)
 *
 * White-label (logo, cor, nome) NÃO vive aqui: é carregado dinamicamente da
 * API no boot (ADR-016, RN-M10-002) pelo WhiteLabelContext/ThemeProvider.
 */

export interface AppRuntimeConfig {
  /** URL base da API (API Gateway). */
  readonly apiUrl: string;
  /** Configuração do Cognito para o fluxo de login/MFA (SPA pública, sem secret). */
  readonly cognito: CognitoRuntimeConfig;
}

export interface CognitoRuntimeConfig {
  /** ID do User Pool do Cognito. */
  readonly userPoolId: string;
  /** ID do app client público (sem secret) do Cognito — fluxo SRP. */
  readonly userPoolClientId: string;
  /** Região AWS onde o User Pool vive (sa-east-1). */
  readonly region: string;
}

function readRequired(name: string, value: string | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error(
      `${name} ausente. A plataforma injeta esta variável no build — não há ` +
        'default, porque um default apontaria para um recurso inexistente.',
    );
  }
  return value;
}

/**
 * Lê e valida a configuração de runtime.
 * @throws Error se alguma variável obrigatória estiver ausente.
 */
export function loadRuntimeConfig(): AppRuntimeConfig {
  const apiUrl = readRequired('VITE_API_URL', import.meta.env.VITE_API_URL);
  const userPoolId = readRequired('VITE_USER_POOL_ID', import.meta.env.VITE_USER_POOL_ID);
  const userPoolClientId = readRequired(
    'VITE_USER_POOL_CLIENT_ID',
    import.meta.env.VITE_USER_POOL_CLIENT_ID,
  );
  const region = readRequired('VITE_AWS_REGION', import.meta.env.VITE_AWS_REGION);

  return {
    apiUrl,
    cognito: { userPoolId, userPoolClientId, region },
  };
}
